import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { surJournal, surLiaison } from '../api/client';
import { STATUT_LABEL } from '../lib/format';

export function Badge({ statut, libelle }: { statut: string; libelle?: string }) {
  const pulse = statut === 'retard' || statut === 'non_restitue';
  return (
    <span className={`badge ${statut}`}>
      {pulse && <i className="pt" />}
      {libelle ?? STATUT_LABEL[statut] ?? statut}
    </span>
  );
}

export function Carte({ titre, action, children, className = '', pad = true }:
  { titre?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; pad?: boolean }) {
  return (
    <section className={`carte ${className}`}>
      {titre && (
        <div className="carte-t">
          <h3 style={{ flex: 1 }}>{titre}</h3>
          {action}
        </div>
      )}
      <div className={pad ? 'carte-p' : ''}>{children}</div>
    </section>
  );
}

export function Kpi({ libelle, valeur, detail, className = '', to }:
  { libelle: string; valeur: ReactNode; detail?: ReactNode; className?: string; to?: string }) {
  const contenu = (
    <>
      <div className="micro">{libelle}</div>
      <div className="v">{valeur}</div>
      {detail && <div className="d">{detail}</div>}
    </>
  );
  // Un chiffre qui parle des voitures doit mener aux voitures : c'est le chemin
  // que cherche quelqu'un qui vient de lire « taux d'utilisation : 33 % ».
  if (to) {
    return (
      <Link to={to} className={`carte carte-p kpi kpi-lien ${className}`}>
        {contenu}
        <span className="kpi-fleche" aria-hidden="true">›</span>
      </Link>
    );
  }
  return <div className={`carte carte-p kpi ${className}`}>{contenu}</div>;
}

export function Bandeau({ type = 'info', titre, children }:
  { type?: 'info' | 'ok' | 'warn' | 'ko' | 'accent'; titre?: string; children?: ReactNode }) {
  return (
    <div className={`bandeau ${type}`} role={type === 'ko' ? 'alert' : undefined}>
      <div>{titre && <b>{titre}</b>}{children}</div>
    </div>
  );
}

export function Champ({ label, aide, erreur, ok, children }:
  { label: string; aide?: ReactNode; erreur?: ReactNode; ok?: boolean; children: ReactNode }) {
  return (
    <div className={`champ ${erreur ? 'ko' : ok ? 'ok' : ''}`}>
      <label>{label}</label>
      {children}
      {erreur ? <span className="err">{erreur}</span> : aide ? <span className="aide">{aide}</span> : null}
    </div>
  );
}

export function Vide({ titre, children, action }: { titre: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="vide"><h3>{titre}</h3>{children && <p>{children}</p>}{action && <div style={{ marginTop: 14 }}>{action}</div>}</div>;
}

export function Squelette({ lignes = 4 }: { lignes?: number }) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {Array.from({ length: lignes }).map((_, i) => (
        <div key={i} className="squelette" style={{ width: `${100 - i * 7}%` }} />
      ))}
    </div>
  );
}

export function Panneau({ titre, onFermer, children, largeur }:
  { titre: ReactNode; onFermer: () => void; children: ReactNode; largeur?: number }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onFermer();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onFermer]);
  return (
    <div className="voile" onClick={onFermer}>
      <div className="panneau" style={largeur ? { width: `min(${largeur}px,100%)` } : undefined} onClick={(e) => e.stopPropagation()}>
        <div className="entete">
          <div className="titres"><h2>{titre}</h2></div>
          <button className="btn btn-f" onClick={onFermer}>Fermer</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Modale({ titre, onFermer, children }: { titre: ReactNode; onFermer: () => void; children: ReactNode }) {
  return (
    <div className="voile centre" onClick={onFermer}>
      <div className="modale" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>{titre}</h2>
        {children}
      </div>
    </div>
  );
}

/** Bandeau hors ligne — le vrai filet du terrain. */
/**
 * Deux pannes differentes, deux messages differents.
 *
 * `navigator.onLine` ne voit que la carte reseau de l'appareil : il reste VRAI
 * quand le Wi-Fi marche mais que le serveur, lui, ne repond plus. C'etait le cas
 * le plus frequent, et le moins visible — l'ecran restait en squelette gris,
 * indefiniment, sans un mot.
 */
export function IndicateurReseau() {
  const [enLigne, setEnLigne] = useState(navigator.onLine);
  const [serveurMuet, setServeurMuet] = useState(false);
  const [tracePerdue, setTracePerdue] = useState(false);
  useEffect(() => surJournal(setTracePerdue), []);
  useEffect(() => {
    const on = () => setEnLigne(true), off = () => setEnLigne(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  useEffect(() => surLiaison(setServeurMuet), []);

  /*
   * Une operation a reussi mais n'a pas laisse de trace. C'est le silence le
   * plus couteux du systeme : sans journal, il n'y a aucune preuve de ce qui a
   * ete fait — et c'est precisement ce qui remplace la facture ici.
   */
  if (tracePerdue) {
    return (
      <div style={{ marginBottom: 14 }}>
        <Bandeau type="ko" titre="Une opération n'a pas été enregistrée au journal">
          L'opération elle-même a bien eu lieu, mais <b>sa trace n'a pas pu être écrite</b>.
          Les opérations suivantes sont bloquées tant que le journal ne répond pas.
          <b> Prévenez Hichem</b> : il faut noter ce qui vient d'être fait avant de continuer.
        </Bandeau>
      </div>
    );
  }

  if (!enLigne) {
    return <div className="hors-ligne">Mode hors ligne — vos saisies seront synchronisées au retour du réseau</div>;
  }
  if (!serveurMuet) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <Bandeau type="ko" titre="Le serveur ne répond pas">
        Votre appareil est connecté, mais le logiciel n'arrive pas à joindre le serveur.
        Les écrans peuvent rester vides tant que la liaison n'est pas rétablie.
        <b> Rien n'est perdu</b> : aucune de vos saisies enregistrées n'est concernée.
        <div className="actions" style={{ marginTop: 12 }}>
          <button className="btn btn-p" onClick={() => window.location.reload()}>Réessayer</button>
        </div>
      </Bandeau>
    </div>
  );
}
